import { ScrollCarousel } from "theme";

/**
 * Scroll-snap card carousel with optional autoplay and clone-based infinite loop.
 * Extends Impact ScrollCarousel; fails open as plain .scroll-area if this module never loads.
 *
 * Center vs start: do not override select(). ScrollCarousel._transitionTo already
 * reads computed scroll-snap-align and uses the card-center formula when it is
 * "center", so carousel:select / carousel:change / edge events stay intact.
 *
 * Loop: clones are not in `items` (selector :not(.is-clone)). next()/previous()
 * animate onto the adjacent clone at the real edges, then jump to the real card.
 */
class SbAutoplayCarousel extends ScrollCarousel {
  connectedCallback() {
    this._sbAbort = new AbortController();
    this._idleTimer = null;
    this._tickTimer = null;
    this._settleTimer = null;
    this._pausedReasons = new Set();
    this._autoplayEnabled = false;
    this._loopEnabled = (this.dataset.loop || "").toLowerCase() === "true";
    this._jumping = false;

    if (this._loopEnabled) {
      this.closest(".sb-b2b-showcase")?.classList.add("is-loop-ready");
      this._sanitizeClones();
    }

    if (typeof super.connectedCallback === "function") {
      super.connectedCallback();
    }

    const signal = this._sbAbort.signal;

    if (this._loopEnabled) {
      this.addEventListener("carousel:settle", () => this._onLoopSettle(), { signal });
      this.addEventListener("scrollend", () => this._onLoopSettle(), { signal });
      this.addEventListener("scroll", () => this._onLoopScrollDebounce(), { signal, passive: true });
      this.offsetWidth;
      this._jumpToReal(0, { silent: true });
      requestAnimationFrame(() => this._jumpToReal(0, { silent: true }));
    }

    this._bindDesignModeSelect();

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const designMode = Boolean(window.Shopify?.designMode);
    const autoplayAttr = (this.dataset.autoplay || "").toLowerCase() === "true";

    if (!autoplayAttr || reducedMotion || designMode) return;

    this._autoplayEnabled = true;
    this._speedMs = Math.max(2, Number(this.dataset.autoplaySpeed) || 3) * 1000;

    this._onPointerEnter = () => this._pause("hover");
    this._onPointerLeave = () => this._resume("hover");
    this._onFocusIn = () => this._pause("focus");
    this._onFocusOut = () => {
      if (!this.contains(document.activeElement)) this._resume("focus");
    };
    this._onUserInteract = () => this._bumpIdle();
    this._onVisibility = () => {
      if (document.hidden) this._pause("pagehide");
      else this._resume("pagehide");
    };

    this.addEventListener("pointerenter", this._onPointerEnter, { signal });
    this.addEventListener("pointerleave", this._onPointerLeave, { signal });
    this.addEventListener("focusin", this._onFocusIn, { signal });
    this.addEventListener("focusout", this._onFocusOut, { signal });
    this.addEventListener("pointerdown", this._onUserInteract, { signal });
    this.addEventListener("touchstart", this._onUserInteract, { signal, passive: true });
    this.addEventListener("wheel", this._onUserInteract, { signal, passive: true });
    document.addEventListener("visibilitychange", this._onVisibility, { signal });

    if (typeof IntersectionObserver === "function") {
      this._io = new IntersectionObserver(
        (entries) => {
          const visible = entries.some((entry) => entry.isIntersecting);
          if (visible) this._resume("viewport");
          else this._pause("viewport");
        },
        { threshold: 0.2 }
      );
      this._io.observe(this);
    }

    this._scheduleTick();
  }

  disconnectedCallback() {
    this._teardown();
    if (typeof super.disconnectedCallback === "function") {
      super.disconnectedCallback();
    }
  }

  next(animate = true) {
    if (!this._loopEnabled) return super.next(animate);
    const count = this.items.length;
    if (count === 0) return;
    if (this.selectedIndex >= count - 1) {
      this._animateToCard(this._allCards()[2 * count], animate !== false);
      return;
    }
    super.next(animate);
  }

  previous(animate = true) {
    if (!this._loopEnabled) return super.previous(animate);
    const count = this.items.length;
    if (count === 0) return;
    if (this.selectedIndex <= 0) {
      this._animateToCard(this._allCards()[count - 1], animate !== false);
      return;
    }
    super.previous(animate);
  }

  _onCarouselScroll() {
    if (!this._loopEnabled) {
      super._onCarouselScroll();
      return;
    }
    if (this._hasPendingProgrammaticScroll || this._jumping || this._scrollArea?.scrollDirection === "none") {
      return;
    }
    const realIndex = this._realIndexFromCentered();
    if (realIndex === null || realIndex === this.selectedIndex) return;
    this._selectedIndex = realIndex;
    this._dispatchEvent("carousel:select", realIndex);
    this._dispatchEvent("carousel:change", realIndex);
  }

  _onLoopScrollDebounce() {
    if (!this._loopEnabled || this._jumping) return;
    window.clearTimeout(this._settleTimer);
    this._settleTimer = window.setTimeout(() => this._onLoopSettle(), 150);
  }

  _onLoopSettle() {
    if (!this._loopEnabled || this._jumping) return;
    window.clearTimeout(this._settleTimer);
    this._settleTimer = null;

    const centered = this._centeredCard();
    if (!centered) return;

    const realIndex = Number(centered.dataset.sbSlideIndex);
    if (!Number.isInteger(realIndex) || realIndex < 0) return;

    if (centered.classList.contains("is-clone")) {
      this._jumpToReal(realIndex);
      return;
    }

    if (realIndex !== this.selectedIndex) {
      this._selectedIndex = realIndex;
      this._dispatchEvent("carousel:select", realIndex);
      this._dispatchEvent("carousel:change", realIndex);
      this._dispatchEvent("carousel:settle", realIndex);
    }
  }

  _jumpToReal(realIndex, { silent = false } = {}) {
    const slide = this.items[realIndex];
    if (!slide) return;

    this._jumping = true;
    const amount = this._scrollAmountFor(slide);
    const previousBehavior = this.style.scrollBehavior;
    this.style.scrollBehavior = "auto";
    this.scrollLeft = amount;
    this.style.scrollBehavior = previousBehavior;
    this._selectedIndex = realIndex;
    this._hasPendingProgrammaticScroll = false;

    this.items.forEach((item) => item.classList.remove("is-selected"));
    slide.classList.add("is-selected");

    if (!silent) {
      this._dispatchEvent("carousel:select", realIndex);
      this._dispatchEvent("carousel:change", realIndex);
      this._dispatchEvent("carousel:settle", realIndex);
    }

    requestAnimationFrame(() => {
      this._jumping = false;
    });
  }

  _animateToCard(card, animate = true) {
    if (!card) return;
    const shouldAnimate = animate !== false;
    this._hasPendingProgrammaticScroll = shouldAnimate;
    this.scrollTo({
      left: this._scrollAmountFor(card),
      behavior: shouldAnimate ? "smooth" : "auto",
    });
  }

  _scrollAmountFor(slide) {
    const slideRect = slide.getBoundingClientRect();
    const scrollerRect = this.getBoundingClientRect();
    if (this._extractSlideAlign(slide) === "center") {
      const slideCenter = slideRect.left + slideRect.width / 2;
      const viewCenter = scrollerRect.left + this.clientWidth / 2;
      return this.scrollLeft + (slideCenter - viewCenter);
    }
    return this.scrollLeft + (slideRect.left - scrollerRect.left);
  }

  _allCards() {
    return Array.from(this.querySelectorAll(".sb-b2b-card"));
  }

  _centeredCard() {
    const viewCenter = this.getBoundingClientRect().left + this.clientWidth / 2;
    let best = null;
    let bestDist = Infinity;
    this._allCards().forEach((card) => {
      if (card.offsetParent === null) return;
      const rect = card.getBoundingClientRect();
      const dist = Math.abs(rect.left + rect.width / 2 - viewCenter);
      if (dist < bestDist) {
        bestDist = dist;
        best = card;
      }
    });
    return best;
  }

  _realIndexFromCentered() {
    const centered = this._centeredCard();
    if (!centered) return null;
    const realIndex = Number(centered.dataset.sbSlideIndex);
    return Number.isInteger(realIndex) && realIndex >= 0 ? realIndex : null;
  }

  _sanitizeClones() {
    this.querySelectorAll(".is-clone a, .is-clone button, .is-clone input, .is-clone select, .is-clone textarea").forEach((el) => {
      el.setAttribute("tabindex", "-1");
    });
  }

  _bindDesignModeSelect() {
    if (!window.Shopify?.designMode) return;
    const section = this.closest(".shopify-section");
    if (!section) return;

    section.addEventListener(
      "shopify:block:select",
      (event) => {
        const selector = this.getAttribute("selector") || ".sb-b2b-card:not(.is-clone)";
        const cards = Array.from(this.querySelectorAll(selector));
        const card = event.target?.closest?.(selector) || event.target;
        const index = cards.indexOf(card);
        if (index >= 0) {
          this.select(index, { animate: !event.detail?.load });
        }
      },
      { signal: this._sbAbort.signal }
    );
  }

  _pause(reason) {
    this._pausedReasons.add(reason);
    this._clearTick();
  }

  _resume(reason) {
    this._pausedReasons.delete(reason);
    if (this._pausedReasons.size === 0 && this._autoplayEnabled) {
      this._scheduleTick();
    }
  }

  _bumpIdle() {
    this._pause("idle");
    window.clearTimeout(this._idleTimer);
    this._idleTimer = window.setTimeout(() => {
      this._idleTimer = null;
      this._resume("idle");
    }, 5000);
  }

  _clearTick() {
    window.clearTimeout(this._tickTimer);
    this._tickTimer = null;
  }

  _scheduleTick() {
    if (!this._autoplayEnabled || this._pausedReasons.size > 0) return;
    this._clearTick();
    this._tickTimer = window.setTimeout(() => this._tick(), this._speedMs);
  }

  _tick() {
    this._tickTimer = null;
    if (!this._autoplayEnabled || this._pausedReasons.size > 0) return;

    if (this._loopEnabled) {
      this.next();
    } else {
      const atEnd = this.scrollLeft + this.clientWidth >= this.scrollWidth - 2;
      if (atEnd) this.select(0);
      else this.next();
    }
    this._scheduleTick();
  }

  _teardown() {
    this._clearTick();
    window.clearTimeout(this._idleTimer);
    window.clearTimeout(this._settleTimer);
    this._idleTimer = null;
    this._settleTimer = null;
    this._pausedReasons.clear();
    this._autoplayEnabled = false;
    this._loopEnabled = false;
    this._jumping = false;
    if (this._io) {
      this._io.disconnect();
      this._io = null;
    }
    if (this._sbAbort) {
      this._sbAbort.abort();
      this._sbAbort = null;
    }
  }
}

if (!customElements.get("sb-autoplay-carousel")) {
  customElements.define("sb-autoplay-carousel", SbAutoplayCarousel);
}
